import { MdCheck, MdClose } from "react-icons/md";
import Modal from "../Modal/Modal";
import { useTranslation } from "react-i18next";
import { ETransactionType, ICON_SIZES } from "src/helpers/constants";
import clsx from "clsx";
import CustomSelect from "../Select/CustomSelect";
import { useMemo, useState } from "react";
import useCurrentUser from "src/hooks/useCurrentUser";
import useAllTransactions from "src/hooks/useAllTransactions";
import InfoBlock from "../InfoBlock/InfoBlock";
import TextInput from "../TextInput/TextInput";
import { EButtonKind } from "../Button/ButtonTypes";

interface IImportReceivedTransaction {
  onClose: () => void;
  onConfirm: (data: any) => void;
  transaction: any;
  isLoading?: boolean;
}

const ImportReceivedTransaction: React.FC<IImportReceivedTransaction> = ({
  onClose,
  onConfirm,
  transaction,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const { allClientProvidersObject } = useAllTransactions();
  const { enabledAccountsList } = useCurrentUser();

  const transactionTypeOptions = useMemo(() => {
    return [
      { value: ETransactionType.INCOME, label: "Ingreso" },
      { value: ETransactionType.EXPENSE, label: "Gasto" },
    ];
  }, []);
  const [importAs, setImportAs] = useState<ETransactionType | string>(
    transaction?.type
  );
  const [targetAccount, setTargetAccount] = useState<string>("");
  const [payTo, setPayTo] = useState<string>(transaction?.payTo);
  const [date, setDate] = useState<string>(transaction?.date);

  const canImport = useMemo(() => {
    return targetAccount && importAs && payTo && date;
  }, [targetAccount, importAs, payTo, date]);

  const accountsList = useMemo(() => {
    return enabledAccountsList?.map?.((account: any) => {
      return {
        value: account?.account,
        label: account?.name,
      };
    });
  }, []);

  return (
    <Modal onEscape={() => onClose()} className="md:!w-[500px]">
      <Modal.Header title="Importar transacción" onClose={() => onClose()} />
      <Modal.Body>
        <div className={clsx(["flex", "gap-4", "flex-col", "w-full"])}>
          <InfoBlock
            message="Podrás editar la transacción después de importarla."
            displayMobileIcon
          />
          <div
            className={clsx([
              "grid",
              "grid-cols-1",
              "md:grid-cols-2",
              "w-full",
              "gap-4",
            ])}
          >
            <TextInput
              type="date"
              value={date}
              onChange={(e: any) => setDate(e?.target?.value)}
              required
              label="Fecha"
            />
            <CustomSelect
              options={transactionTypeOptions}
              isClearable={false}
              isSearchable={false}
              onChange={(e: any) => setImportAs(e?.value)}
              value={importAs}
              label="Tipo"
              required
              useValueAsLabel={false}
              placeholder="Seleccionar"
            />
          </div>
          {importAs && (
            <>
              <CustomSelect
                options={allClientProvidersObject}
                onChange={(e: any) => setPayTo(e?.value)}
                value={payTo}
                label={
                  importAs === ETransactionType.EXPENSE ? "Destino" : "Origen"
                }
                required
                placeholder="Seleccionar"
                creatable
              />
            </>
          )}
          <CustomSelect
            options={accountsList}
            onChange={(e: any) => setTargetAccount(e?.value)}
            value={targetAccount}
            label="Importar a"
            required
            useValueAsLabel={false}
            placeholder="Seleccionar"
          />
        </div>
      </Modal.Body>
      <Modal.Footer
        actions={[
          {
            text: t("Importar"),
            onClick: () =>
              onConfirm({
                account: targetAccount,
                type: importAs,
                payTo,
                date,
              }),
            icon: <MdCheck size={ICON_SIZES.MD} />,
            kind: EButtonKind.SAVE,
            disabled: !canImport || isLoading,
            isLoading,
          },
          {
            text: t("common_buttons.close"),
            onClick: () => onClose(),
            kind: EButtonKind.NORMAL,
            icon: <MdClose size={ICON_SIZES.MD} />,
            disabled: isLoading,
          },
        ]}
      />
    </Modal>
  );
};

export default ImportReceivedTransaction;
